import { useState, useEffect, useRef, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import type { ProjectFieldKey } from '@/contexts/FieldConfigContext';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TextareaWithCount } from '@/components/ui/textarea-with-count';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Plus, X, Minus, Sparkles, Star, Info, AlertTriangle, Briefcase, ArrowRight, WandSparkles } from 'lucide-react';
import { format, addDays, addWeeks, addMonths, addYears, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears } from 'date-fns';
import { cn } from '@/lib/utils';
import { useFieldConfig } from '@/contexts/FieldConfigContext';
import { MultiSelect } from '@/components/ui/multiselect';
import { MultiSelectWithFavorites } from '@/components/ui/multiselect-with-favorites';
import { useIntegrationSettings } from '@/hooks/use-integration-settings';
import { Combobox } from '@/components/ui/combobox';
import type { DurationUnit } from '@/contexts/FieldConfigContext';
import { fetchClients, fetchEmployees, getDataMode, checkProjectCodeExists } from '@/lib/data-service';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { PROJECT_STATUS_LABELS } from '@/types';

// Function to create dynamic form validation schema based on required fields
const createProjectFormSchema = (
  requiredFields: ProjectFieldKey[],
  timelineCompletionRequired: boolean,
  maxLengths?: { projectCode?: number; projectName?: number; description?: number }
) => {
  const isRequired = (field: ProjectFieldKey) => requiredFields.includes(field);
  
  return z.object({
    // Always required fields (isAlwaysMandatory)
    projectName: z.string()
      .min(1, 'Project name is required')
      .max(maxLengths?.projectName || 80, `Project name must not exceed ${maxLengths?.projectName || 80} characters`),
    projectCode: z.string()
      .min(1, 'Project code is required')
      .max(maxLengths?.projectCode || 10, `Project code must not exceed ${maxLengths?.projectCode || 10} characters`),
    // Configurable fields - required based on settings
    description: maxLengths?.description 
      ? z.string().max(maxLengths.description, `Description must not exceed ${maxLengths.description} characters`).optional()
      : z.string().optional(),
    status: isRequired('status') 
      ? z.string().min(1, 'Status is required') 
      : z.string().optional(),
    priority: isRequired('priority') 
      ? z.string().min(1, 'Priority is required') 
      : z.string().optional(),
    type: isRequired('type') 
      ? z.string().min(1, 'Type is required') 
      : z.string().optional(),
    location: z.string().optional(),
    // Timeline fields - handle timeline completion requirement
    startDate: (timelineCompletionRequired || isRequired('startDate'))
      ? z.date({ message: 'Start date is required' }) 
      : z.date().optional(),
    endDate: isRequired('endDate') 
      ? z.date({ message: 'End date is required' }) 
      : z.date().optional(),
    duration: isRequired('duration') 
      ? z.number({ message: 'Duration is required' }).min(1, 'Duration must be at least 1') 
      : z.number().optional(),
    durationUnit: z.enum(['days', 'weeks', 'months', 'years']).optional(),
    projectOwner: z.string().optional(),
    projectManager: isRequired('projectManager') 
      ? z.string().min(1, 'Project manager is required') 
      : z.string().optional(),
    projectManagers: z.array(z.string()).optional(),
    clients: isRequired('clients') 
      ? z.array(z.string()).min(1, 'Client is required') 
      : z.array(z.string()).optional(),
    currencyCode: z.string().optional(),
    budget: isRequired('budget') 
      ? z.string().min(1, 'Budget is required').refine(
          (val) => {
            // Validate format without parsing to number (preserves precision)
            const cleaned = val.replace(/[^0-9.-]+/g, '');
            // Max 16 digits before decimal + 2 after for DECIMAL(18,2)
            const digitsBeforeDecimal = cleaned.split('.')[0].replace(/[^0-9]/g, '').length;
            return digitsBeforeDecimal <= 16 && !cleaned.startsWith('-');
          },
          { message: 'Budget exceeds maximum allowed value' }
        )
      : z.string().optional().refine(
          (val) => {
            if (!val) return true;
            // Validate format without parsing to number (preserves precision)
            const cleaned = val.replace(/[^0-9.-]+/g, '');
            // Max 16 digits before decimal + 2 after for DECIMAL(18,2)
            const digitsBeforeDecimal = cleaned.split('.')[0].replace(/[^0-9]/g, '').length;
            return digitsBeforeDecimal <= 16 && !cleaned.startsWith('-');
          },
          { message: 'Budget exceeds maximum allowed value' }
        ),
    tags: z.array(z.string()).optional(),
    attachments: z.array(z.any()).optional(),
  }).refine(
    (data) => {
      // If timeline completion is required, validate that user has filled Start Date + (End Date OR Duration)
      if (timelineCompletionRequired) {
        const hasStartDate = !!data.startDate;
        const hasEndDate = !!data.endDate;
        const hasDuration = !!data.duration && data.duration > 0;
        
        // Must have start date AND (end date OR duration)
        return hasStartDate && (hasEndDate || hasDuration);
      }
      return true;
    },
    {
      message: 'Timeline completion requires Start Date and either End Date or Duration',
      path: ['startDate'], // Show error on startDate field
    }
  );
};

// Default schema for type inference (will be overridden with dynamic schema in component)
const projectFormSchema = createProjectFormSchema(['projectName', 'projectCode'], false);

type ProjectFormValues = z.infer<typeof projectFormSchema>;

// Custom field type
interface CustomField {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'boolean' | 'dropdown' | 'multiselect';
  defaultValue?: string;
  options?: string[];
}

interface ProjectTemplate {
  id: string;
  name: string;
  description?: string;
  templateDescription?: string;
  isFavorite: boolean;
  // Fields that can be pre-filled from template
  priority?: string;
  type?: string;
  location?: string;
  currencyCode?: string;
  clients?: string[];
  tags?: string[];
}

interface ProjectFormProps {
  onSubmit: (data: ProjectFormValues & { customFields: CustomField [] }) => void;
  onCancel: () => void;
  onMinimize?: (data: Partial<ProjectFormValues>) => void;
  initialData?: Partial<ProjectFormValues>;
  isMinimized?: boolean;
  onProgressChange?: (progress: number) => void;
  onIncompleteFieldsChange?: (fields: string[]) => void;
  onFormDataChange?: (data: Partial<ProjectFormValues>) => void;
  quickCreateMode?: boolean;
  onShowFullDetails?: () => void;
  onHideFullDetails?: () => void;
  showFullDetails?: boolean;
  // Template and AI support for Killer UX
  templates?: ProjectTemplate[];
  onTemplateSelect?: (templateId: string) => void;
  selectedTemplate?: string;
  onAiAssist?: () => void;
  highlightedField?: string | null;
  onManageTemplates?: () => void;
}

// Mock data for dropdowns
const priorityOptions = ['Low', 'Medium', 'High', 'Critical'];
const typeOptions = ['Fixed Price', 'Time & Materials', 'Retainer'];
const projectManagerOptions = ['John Smith', 'Sarah Johnson', 'Michael Chen', 'Emily Davis'];
const clientOptions = [
  'Acme Corp',
  'TechStart Inc',
  'Global Solutions',
  'Innovation Labs',
  'Alpha Industries',
  'Beta Technologies',
  'Gamma Enterprises',
  'Delta Systems',
  'Epsilon Group',
  'Zeta Corporation',
  'Theta Ventures',
  'Iota Partners',
  'Kappa Holdings',
  'Lambda Consulting',
  'Mu Dynamics',
  'Nu Solutions',
  'Xi Innovations',
  'Omicron Tech',
  'Pi Digital',
  'Rho Analytics',
  'Sigma Networks',
  'Tau Services',
  'Upsilon Labs',
  'Phi Enterprises',
  'Chi Systems',
  'Psi Group',
  'Omega Industries',
  'Apex Corporation',
  'Zenith Partners',
  'Vertex Solutions',
  'Summit Technologies',
  'Horizon Ventures',
  'Nexus Group',
  'Quantum Systems',
  'Fusion Enterprises',
  'Catalyst Inc',
  'Momentum Partners',
  'Velocity Corp',
  'Pinnacle Solutions',
  'Vanguard Tech',
];

const tagOptions= ['Urgent', 'Strategic', 'Revenue', 'Innovation', 'Compliance', 'Marketing', 'Sales', 'Operations'];

// Default empty array for templates prop - defined outside component to maintain stable reference
const EMPTY_TEMPLATES: ProjectTemplate[] = [];

// Helper functions for budget formatting with thousand separators
const formatBudgetDisplay = (value: string): string => {
  if (!value) return '';
  // Remove any existing formatting
  const cleaned = value.replace(/[^0-9.]/g, '');
  if (!cleaned) return '';
  
  const parts = cleaned.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  
  // Return with decimal part if present
  if (decimalPart !== undefined) {
    return `${formattedInteger}.${decimalPart}`;
  }
  return formattedInteger;
};

const parseBudgetValue = (formattedValue: string): string => {
  // Remove thousand separators to get raw value
  return formattedValue.replace(/,/g, '');
};

// Helper component for labels with tooltip and template indicator
const LabelWithTooltip = ({ label, tooltip, align = "center", isFromTemplate = false }: { label: string; tooltip?: string; align?: "start" | "center" | "end"; isFromTemplate?: boolean }) => {
  const labelContent = (
    <div className="flex items-center gap-1.5">
      <FormLabel>{label}</FormLabel>
      {isFromTemplate && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-700 rounded-full">
          From template
        </span>
      )}
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" align={align} className="max-w-[300px]">
            <p className="text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
  
  return labelContent;
};

export function ProjectForm({ 
  onSubmit, 
  onCancel,
  onMinimize,
  initialData, 
  isMinimized, 
  onProgressChange, 
  onIncompleteFieldsChange,
  onFormDataChange,
  quickCreateMode = false,
  onShowFullDetails,
  onHideFullDetails,
  showFullDetails = true,
  templates = EMPTY_TEMPLATES,
  onTemplateSelect,
  selectedTemplate,
  onAiAssist,
  highlightedField,
  onManageTemplates,
}: ProjectFormProps) {
  const { requiredFields, fieldConfigs, getFieldConfig, timelineSettings, generateProjectCode } = useFieldConfig();
  const integrationSettings = useIntegrationSettings();
  const { selectableStatuses } = useProjectStatuses();

  // Build status options from context or fallback to PROJECT_STATUS_LABELS
  const statusOptions = useMemo(() => {
    if (selectableStatuses.length > 0) {
      return selectableStatuses.map(s => ({ value: String(s.id), label: s.name }));
    }
    return Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => ({ value: id, label }));
  }, [selectableStatuses]);
  
  // Fields to show in Quick Create mode
  // When timeline completion is required, include all timeline fields regardless of individual required status
  const quickCreateFields = useMemo(() => {
    if (timelineSettings.timelineCompletionRequired) {
      // Add timeline fields if not already in required fields
      const timelineFields: ProjectFieldKey[] = ['startDate', 'endDate', 'duration'];
      const fieldsSet = new Set([...requiredFields, ...timelineFields]);
      return Array.from(fieldsSet);
    }
    return requiredFields;
  }, [requiredFields, timelineSettings.timelineCompletionRequired]);
  
  // Check if there are any optional fields (fields that are not required)
  const hasOptionalFields = useMemo(() => {
    return fieldConfigs.some(config => !config.isRequired);
  }, [fieldConfigs]);
  
  // Create dynamic schema based on required fields from settings
  const dynamicSchema = useMemo(() => {
    return createProjectFormSchema(
      requiredFields,
      timelineSettings.timelineCompletionRequired,
      {
        projectCode: integrationSettings.projectCodeMaxLength,
        projectName: integrationSettings.projectNameMaxLength,
        description: integrationSettings.descriptionMaxLength,
      }
    );
  }, [requiredFields, timelineSettings.timelineCompletionRequired, integrationSettings]);
  
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  // Initialize hasFormChanges to true if initialData is provided (form was restored from draft)
  const [hasFormChanges, setHasFormChanges] = useState(!!initialData);
  
  // Refs for field elements to enable scrolling
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // AI form state
  const [aiProjectDescription, setAiProjectDescription] = useState('');
  const [aiObjectives, setAiObjectives] = useState('');
  const [aiTimeline, setAiTimeline] = useState('');
  
  // Date picker popover state
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  // Controlled month state for End Date calendar to ensure it shows the selected date when reopened
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(undefined);
  
  // Toggle between End Date mode and Duration mode - use settings default
  const [isEndDateMode, setIsEndDateMode] = useState(timelineSettings.defaultToEndDate);
  
  // Track which field was last edited for two-way binding between end date and duration
  const [lastEditedField, setLastEditedField] = useState<'endDate' | 'duration' | null>(null);
  const [showDurationCapWarning, setShowDurationCapWarning] = useState(false);
  
  // Track which fields were pre-filled from a template
  const [templatePrefilledFields, setTemplatePrefilledFields] = useState<Set<string>>(new Set());
  
  // Project code uniqueness validation state
  const [isCheckingProjectCode, setIsCheckingProjectCode] = useState(false);
  const [projectCodeExists, setProjectCodeExists] = useState(false);
  const projectCodeCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Maximum duration values for each unit
  const MAX_DURATION_VALUES: Record<DurationUnit, number> = {
    days: 18250,   // ~50 years
    weeks: 2600,   // ~50 years
    months: 600,   // 50 years
    years: 50      // 50 years
  };

  const getMaxDuration = (unit: DurationUnit): number => {
    return MAX_DURATION_VALUES[unit];
  };

  // Helper functions for duration calculations
  const addDurationToDate = (startDate: Date, value: number, unit: DurationUnit): Date => {
    switch (unit) {
      case 'days':
        return addDays(startDate, value);
      case 'weeks':
        return addWeeks(startDate, value);
      case 'months':
        return addMonths(startDate, value);
      case 'years':
        return addYears(startDate, value);
      default:
        return startDate;
    }
  };

  const calculateDurationInUnit = (startDate: Date, endDate: Date, unit: DurationUnit): number => {
    switch (unit) {
      case 'days':
        return differenceInDays(endDate, startDate);
      case 'weeks':
        return Math.floor(differenceInWeeks(endDate, startDate));
      case 'months':
        return differenceInMonths(endDate, startDate);
      case 'years':
        return differenceInYears(endDate, startDate);
      default:
        return 0;
    }
  };

  const canDisplayInUnit = (startDate: Date, endDate: Date, unit: DurationUnit): boolean => {
    if (!startDate || !endDate) return false;
    try {
      const value = calculateDurationInUnit(startDate, endDate, unit);
      return value >= 1;
    } catch (error) {
      return false;
    }
  };

  const formatDurationUnit = (value: number, unit: DurationUnit): string => {
    const singular = unit.slice(0, -1); // Remove 's' from end
    return value === 1 ? singular : unit;
  };

  const getAvailableUnits = (startDate: Date, endDate: Date): DurationUnit[] => {
    if (!startDate || !endDate) return ['days', 'weeks', 'months', 'years'];
    try {
      const units: DurationUnit[] = ['days', 'weeks', 'months', 'years'];
      const filtered = units.filter(unit => canDisplayInUnit(startDate, endDate, unit));
      return filtered.length > 0 ? filtered : ['days']; // Always return at least 'days'
    } catch (error) {
      return ['days', 'weeks', 'months', 'years'];
    }
  };
  
  // Fetched clients for Live mode
  const [fetchedClients, setFetchedClients] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  
  // Fetched employees for Live mode (for Project Manager dropdown)
  const [fetchedEmployees, setFetchedEmployees] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  
  // Fetch clients and employees on mount
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const clients = await fetchClients('project-form');
        // Map to consistent format with id and name
        const mappedClients = clients.map((c: any) => ({
          id: c.id,
          name: c.name
        }));
        setFetchedClients(mappedClients);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      } finally {
        setIsLoadingClients(false);
      }
    };
    
    const loadEmployees= async () => {
      setIsLoadingEmployees(true);
      try {
        const employees = await fetchEmployees('project-form');
        // Map to consistent format with id and name
        const mappedEmployees = employees.map((e: any) => ({
          id: e.id,
          name: e.name
        }));
        setFetchedEmployees(mappedEmployees);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    
    loadClients();
    loadEmployees();
  }, []);
  
  // In Quick Create mode, sections start collapsed unless showFullDetails is true
  const [sectionsOpen, setSectionsOpen] = useState({
    templates: quickCreateMode ? showFullDetails : true,
    general: quickCreateMode ? showFullDetails : true,
    timeline: quickCreateMode ? showFullDetails : true,
    management: quickCreateMode ? showFullDetails : true,
    custom: quickCreateMode ? showFullDetails : true,
  });

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      projectName: initialData?.projectName || '',
      projectCode: initialData?.projectCode || '',
      description: initialData?.description || '',
      status: initialData?.status || '',
      priority: initialData?.priority || '',
      type: initialData?.type || '',
      location: initialData?.location || '',
      startDate: initialData?.startDate,
      endDate: initialData?.endDate,
      duration: initialData?.duration,
      durationUnit: (initialData?.durationUnit as DurationUnit) || timelineSettings.defaultDurationUnit,
      projectOwner: initialData?.projectOwner || '',
      projectManager: initialData?.projectManager || '',
      projectManagers: initialData?.projectManagers || [],
      clients: initialData?.clients || [],
      currencyCode:initialData?.currencyCode || '',
      budget: initialData?.budget || '',
      tags: initialData?.tags || [],
      attachments: initialData?.attachments || [],
    },
  });

  // Calculate form completion percentage
  const calculateProgress = () => {
    // Use dynamic required fields from configuration
    const fieldsToCheck = requiredFields;

    const values = form.getValues();
    
    // Calculate completed required fields
    const completedRequiredFields = fieldsToCheck.filter(field => {
      const value = values[field as keyof ProjectFormValues];
      return value !== undefined && value !== null && value !== '';
    });
    
    // Check if all required fields are complete (for enabling submit button)
    const requiredFieldsComplete = completedRequiredFields.length === fieldsToCheck.length;
    
    // Show incomplete required fields
    const incompleteFields = fieldsToCheck.filter(field => {
      const value = values[field as keyof ProjectFormValues];
      return value === undefined || value === null || value === '';
    }).map(field => {
      const config = getFieldConfig(field);
      return config?.label || field;
    });

    // Calculate progress based on required fields only
    const progress = fieldsToCheck.length > 0 
      ? Math.round((completedRequiredFields.length / fieldsToCheck.length) * 100)
      : 100;

    return {
      progress,
      incompleteFields,
      canSubmit: requiredFieldsComplete,
    };
  };

  // Generate timeline summary for collapsed accordion header
  const getTimelineSummary = () => {
    const values = form.getValues();
    const startDate = values.startDate;
    const endDate = values.endDate;
    const duration = values.duration;
    const durationUnit = values.durationUnit || timelineSettings.defaultDurationUnit;
    
    // Only show summary if we have both start and end dates
    if (!startDate || !endDate) {
      return null;
    }
    
    // Calculate duration if not explicitly set
    let calculatedDuration = duration;
    if (!calculatedDuration && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      switch (durationUnit) {
        case 'days':
          calculatedDuration = differenceInDays(end, start);
          break;
        case 'weeks':
          calculatedDuration = differenceInWeeks(end, start);
          break;
        case 'months':
          calculatedDuration = differenceInMonths(end, start);
          break;
        case 'years':
          calculatedDuration = differenceInYears(end, start);
          break;
      }
    }
    
    // Format dates
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Full format: "15 Jan 2024 - 20 Mar 2024 (65 days)"
    const fullFormat = `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')} (${calculatedDuration} ${durationUnit})`;
    
    // Compact format: "15/01/24 - 20/03/24 (65d)"
    const unitAbbrev = durationUnit.charAt(0); // d, w, m, y
    const compactFormat = `${format(start, 'dd/MM/yy')} - ${format(end, 'dd/MM/yy')} (${calculatedDuration}${unitAbbrev})`;
    
    return {
      full: fullFormat,
      compact: compactFormat,
    };
  };

  const [progress, setProgress] = useState(calculateProgress().progress);
  const [canSubmit, setCanSubmit] = useState(calculateProgress().canSubmit);
  const [localIncompleteFields, setLocalIncompleteFields] = useState<string[]>(calculateProgress().incompleteFields);
  const [timelineSummary, setTimelineSummary] = useState(getTimelineSummary());

  // Generate template summary for collapsed accordion header
  const getTemplateSummary = () => {
    if (!selectedTemplate || selectedTemplate === 'none') {
      return null;
    }
    
    const template = templates.find(t => t.id === selectedTemplate);
    return template?.name || null;
  };

  const [templateSummary, setTemplateSummary] = useState(getTemplateSummary());

  // Update progress and timeline summary when form values change
  useEffect(() => {
    let isInitialMount = true;
    
    const subscription = form.watch(() => {
      const { progress: newProgress, incompleteFields, canSubmit: newCanSubmit } = calculateProgress();
      setProgress(newProgress);
      setCanSubmit(newCanSubmit);
      setLocalIncompleteFields(incompleteFields);
      setTimelineSummary(getTimelineSummary());
      onProgressChange?.(newProgress);
      onIncompleteFieldsChange?.(incompleteFields);
      
      // Skip notifying parent on initial mount
      if (isInitialMount) {
        isInitialMount = false;
        return;
      }
      
      // Notify parent of form data changes for draft saving
      // Only notify if there are actual changes (not just initialization)
      if (hasFormChanges) {
        onFormDataChange?.(form.getValues());
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onProgressChange, onIncompleteFieldsChange, onFormDataChange, hasFormChanges]);

  // Update template summary when selectedTemplate changes
  useEffect(() => {
    setTemplateSummary(getTemplateSummary());
  }, [selectedTemplate, templates]);

  // Initial progress calculation
  useEffect(() => {
    const { progress: initialProgress, incompleteFields, canSubmit: initialCanSubmit } = calculateProgress();
    setProgress(initialProgress);
    setCanSubmit(initialCanSubmit);
    setLocalIncompleteFields(incompleteFields);
    onProgressChange?.(initialProgress);
    onIncompleteFieldsChange?.(incompleteFields);
  }, []);

  // Debug: Log when showCancelWarning changes
  useEffect(() => {
    console.log('showCancelWarning changed to:', showCancelWarning);
  }, [showCancelWarning]);
  
  // Watch for form changes to detect unsaved data
  useEffect(() => {
    let isInitialMount = true;
    
    const subscription = form.watch((value, { name, type }) => {
      // Skip the initial mount trigger
      if (isInitialMount) {
        isInitialMount = false;
        return;
      }
      
      // Check if any field has a non-empty value (excluding fields with default values)
      const hasData = Object.entries(value).some(([key, val]) => {
        // Skip fields that always have default values
        if (key === 'durationUnit') return false;
        
        if (val === null || val === undefined || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      });
      
      console.log('Form changed - field:', name, 'type:', type, 'hasData:', hasData);
      // If we have initialData (restored form), always consider it as having changes
      // Otherwise, check if there's actual data in the form
      setHasFormChanges(!!initialData || hasData || customFields.length > 0);
    });
    
    return () => subscription.unsubscribe();
  }, [form, customFields.length, initialData]);

  // Auto-focus on project name field when modal opens
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      projectNameInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);


  // Debounced project code uniqueness validation
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'projectCode') {
        const code = value.projectCode;
        
        // Clear any pending timeout
        if (projectCodeCheckTimeoutRef.current) {
          clearTimeout(projectCodeCheckTimeoutRef.current);
        }
        
        // Reset state if code is empty
        if (!code || code.trim() === '') {
          setProjectCodeExists(false);
          setIsCheckingProjectCode(false);
          return;
        }
        
        // Set checking state and debounce the API call
        setIsCheckingProjectCode(true);
        projectCodeCheckTimeoutRef.current = setTimeout(async () => {
          try {
            const exists = await checkProjectCodeExists(code.trim());
            setProjectCodeExists(exists);
          } catch (error) {
            console.error('Failed to check project code:', error);
            setProjectCodeExists(false);
          } finally {
            setIsCheckingProjectCode(false);
          }
        }, 300);
      }
    });
    
    return () => {
      subscription.unsubscribe();
      if (projectCodeCheckTimeoutRef.current) {
        clearTimeout(projectCodeCheckTimeoutRef.current);
      }
    };
  }, [form]);

  // Update sections when showFullDetails changes in Quick Create mode
  useEffect(() => {
    if (quickCreateMode) {
      setSectionsOpen((prev) => ({
        templates: prev.templates, // Preserve templates section state
        general: showFullDetails,
        timeline: showFullDetails,
        management: showFullDetails,
        custom: showFullDetails,
      }));
    }
  }, [showFullDetails, quickCreateMode]);

  // Two-way binding: When End Date changes, update Duration
  useEffect(() => {
    if (lastEditedField === 'endDate') {
      const startDate = form.getValues('startDate');
      const endDate = form.getValues('endDate');
      
      if (startDate && endDate && endDate > startDate) {
        const days = differenceInDays(endDate, startDate);
        form.setValue('duration', days, { shouldValidate: false });
      }
      setLastEditedField(null);
    }
  }, [lastEditedField, form]);

  // Two-way binding: When Duration changes, update End Date
  useEffect(() => {
    if (lastEditedField === 'duration') {
      const startDate = form.getValues('startDate');
      const duration = form.getValues('duration');
      
      if (startDate && duration && duration > 0) {
        const calculatedEndDate = addDays(startDate, duration);
        form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
      }
      setLastEditedField(null);
    }
  }, [lastEditedField, form]);

  // Pre-fill form fields when a template is selected
  useEffect(() => {
    if (!selectedTemplate || selectedTemplate === 'none') {
      // Clear pre-filled fields tracking when no template is selected
      setTemplatePrefilledFields(new Set());
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    const prefilledFields = new Set<string>();

    // Pre-fill description from template
    if (template.description) {
      form.setValue('description', template.description, { shouldValidate: true });
      prefilledFields.add('description');
    }

    // Pre-fill priority from template
    if (template.priority) {
      // Convert priority to lowercase to match form values
      form.setValue('priority', template.priority.toLowerCase(), { shouldValidate: true });
      prefilledFields.add('priority');
    }

    // Pre-fill type from template
    if (template.type) {
      form.setValue('type', template.type, { shouldValidate: true });
      prefilledFields.add('type');
    }

    // Pre-fill location from template
    if (template.location) {
      form.setValue('location', template.location, { shouldValidate: true });
      prefilledFields.add('location');
    }

    // Pre-fill currency from template
    if (template.currencyCode) {
      form.setValue('currencyCode', template.currencyCode, { shouldValidate: true });
      prefilledFields.add('currencyCode');
    }

    // Pre-fill clients from template
    if (template.clients && template.clients.length > 0) {
      form.setValue('clients', template.clients, { shouldValidate: true });
      prefilledFields.add('clients');
    }

    // Pre-fill tags from template
    if (template.tags && template.tags.length > 0) {
      form.setValue('tags', template.tags, { shouldValidate: true });
      prefilledFields.add('tags');
    }

    setTemplatePrefilledFields(prefilledFields);
    
    // Mark form as having changes since we pre-filled fields
    if (prefilledFields.size > 0) {
      setHasFormChanges(true);
    }
  }, [selectedTemplate, templates, form]);

  // In Quick Create mode, form can be submitted when minimum required fields are complete
  // In Legacy mode, form requires all required fields (progress === 100)
  const isFormComplete = quickCreateMode ? canSubmit : progress === 100;

  const handleSubmit = (data: ProjectFormValues) => {
    onSubmit({ ...data, customFields });
  };

  // Helper function to get field label with dynamic asterisk
  const getFieldLabel = (fieldKey: string): string => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return fieldKey;
    
    // Dynamically change labels when saving as template
    let label = config.label;
    return `${label}${config.isRequired ? ' *' : ''}`;
  };

  const handleCancelClick = () => {
    console.log('Cancel clicked - hasFormChanges:', hasFormChanges, 'customFields:', customFields.length);
    
    if (hasFormChanges) {
      // Show warning dialog
      console.log('Showing warning dialog');
      setShowCancelWarning(true);
    } else {
      // No unsaved data, close immediately
      console.log('No unsaved data, closing immediately');
      onCancel();
    }
  };

  const handleDiscardChanges = () => {
    setShowCancelWarning(false);
    onCancel();
  };

  const handleSaveAsDraft = () => {
    setShowCancelWarning(false);
    if (onMinimize) {
      // Get current form values
      const currentFormData = form.getValues();
      onMinimize(currentFormData);
    } else {
      // Fallback to cancel if minimize is not available
      onCancel();
    }
  };

  const addCustomField = () => {
    if (customFields.length >= 20) return;
    
    const newField: CustomField = {
      id: `custom_${Date.now()}`,
      fieldKey: '',
      fieldLabel: '',
      fieldType: 'text',
    };
    setCustomFields([...customFields, newField]);
  };

  const removeCustomField = (id: string) => {
    setCustomFields(customFields.filter(f => f.id !== id));
  };

  const updateCustomField = (id: string, updates: Partial<CustomField>) => {
    setCustomFields(customFields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle AI generation
  const handleAiGenerate = () => {
    setAiGenerating(true);
    
    // Simulate AI generation (replace with actual API call)
    setTimeout(() => {
      // Pre-fill form fields with AI-generated data with staggered animation
      const fields = [
        { name: 'projectName', value: aiProjectDescription.split(' ').slice(0, 4).join(' ') || 'AI Generated Project', delay: 0 },
        { name: 'projectCode', value: 'AI-' + Date.now().toString().slice(-6), delay: 100 },
        { name: 'description', value: aiProjectDescription, delay: 200 },
        { name: 'status', value: 'not-started', delay: 300 },
        { name: 'priority', value: 'medium', delay: 400 },
        { name: 'type', value: 'internal', delay: 500 },
      ];

      fields.forEach(({ name, value, delay }) => {
        setTimeout(() => {
          form.setValue(name as any, value);
        }, delay);
      });

      // Set dates with delay
      setTimeout(() => {
        const startDate = new Date();
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 6); // Default 6 months
        form.setValue('startDate', startDate);
        form.setValue('endDate', endDate);
        form.setValue('projectManager', 'john-smith');
      }, 600);
      
      setTimeout(() => {
        setAiGenerating(false);
        setShowAiPanel(false);
        
        // Show full details after AI generation
        if (onShowFullDetails) {
          onShowFullDetails();
        }
      }, 1000);
    }, 1500);
  };

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Esc to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
      // Ctrl/Cmd + Enter to submit (if form is complete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isFormComplete) {
        e.preventDefault();
        form.handleSubmit(handleSubmit)();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFormComplete, onCancel, form]);

  // Handle field highlighting and scrolling
  useEffect(() => {
    console.log('highlightedField changed to:', highlightedField);
    console.log('Available field refs:', Object.keys(fieldRefs.current));
    
    if (highlightedField && fieldRefs.current[highlightedField]) {
      const fieldElement = fieldRefs.current[highlightedField];
      console.log('Field element found:', fieldElement);
      
      if (fieldElement) {
        // Scroll to the field with smooth behaviour
        fieldElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
        console.log('Scrolled to field:', highlightedField);
        
        // Focus the actual input/textarea/select element inside
        const inputElement = fieldElement.querySelector('input, textarea, select') as HTMLElement;
        if (inputElement) {
          // Small delay to ensure scroll completes before focusing
          setTimeout(() => {
            inputElement.focus();
            console.log('Focused input element');
          }, 300);
        }
      }
    } else if (highlightedField) {
      console.log('Field ref not found for:', highlightedField);
    }
  }, [highlightedField]);

  // Helper function to wrap field with ref and highlight styling
  const wrapFieldWithHighlight = (fieldKey: string, fieldContent: React.ReactNode) => {
    return (
      <div 
        key={fieldKey}
        ref={(el) => { fieldRefs.current[fieldKey] = el; }}
        className={cn(
          "transition-all duration-300 rounded-md",
          highlightedField === fieldKey && "ring-2 ring-error-500 ring-offset-2 p-2 -m-2 animate-pulse-scale"
        )}
      >
        {fieldContent}
      </div>
    );
  };

  // Helper function to render a field based on its configuration
  const renderField = (fieldKey: string) => {
    const config = getFieldConfig(fieldKey as any);
    if (!config) return null;

    const isRequired = config.isRequired;
    const label = `${config.label}${isRequired ? ' *' : ''}`;
    const isFromTemplate = templatePrefilledFields.has(fieldKey);

    switch (fieldKey) {
      case 'projectName':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Enter project name" 
                    className="text-lg font-medium"
                    autoComplete="off"
                    aria-label="Project name"
                    aria-required={isRequired}
                    maxLength={integrationSettings.projectNameMaxLength}
                    {...field}
                    ref={(e) => {
                      field.ref(e);
                      if (quickCreateMode) {
                        projectNameInputRef.current = e;
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectCode':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectCode"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-1.5">
                  <FormLabel>{label}</FormLabel>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const projectName = form.getValues('projectName');
                          if (projectName) {
                            const generatedCode = generateProjectCode(projectName, integrationSettings.projectCodeMaxLength);
                            form.setValue('projectCode', generatedCode, { shouldValidate: true });
                          }
                        }}
                      >
                        <WandSparkles className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-sm">Auto-generate code from project name</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      placeholder="e.g., PRJ-2024-001"
                      maxLength={integrationSettings.projectCodeMaxLength}
                      className={cn(
                        projectCodeExists && "border-destructive focus-visible:ring-destructive"
                      )}
                      {...field} 
                    />
                    {isCheckingProjectCode && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                      </div>
                    )}
                  </div>
                </FormControl>
                {projectCodeExists && (
                  <p className="text-sm font-medium text-destructive">
                    A project with this code already exists
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'status':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={statusOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select status"
                    searchPlaceholder="Search status..."
                    emptyMessage="No status found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'priority':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <Combobox
                    options={priorityOptions.map(option => ({ 
                      value: option.toLowerCase(), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select priority"
                    searchPlaceholder="Search priority..."
                    emptyMessage="No priority found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'type':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <Combobox
                    options={typeOptions.map(option => ({ 
                      value: option.toLowerCase().replace(' ', '-'), 
                      label: option 
                    }))}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select type"
                    searchPlaceholder="Search type..."
                    emptyMessage="No type found."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'location':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <Input 
                    placeholder="Enter project location" 
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Physical or organizational location of the project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'startDate':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{label}</FormLabel>
                <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          setStartDateOpen(false);
                          // In Duration mode: Recalculate end date when start date changes
                          if (!isEndDateMode) {
                            const duration = form.getValues('duration');
                            const durationUnit = form.getValues('durationUnit') || 'days';
                            if (date && duration && duration > 0) {
                              const calculatedEndDate = addDurationToDate(date, duration, durationUnit);
                              form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
                            }
                          }
                          // In End Date mode: Recalculate duration when start date changes
                          else {
                            const endDate = form.getValues('endDate');
                            if (date && endDate) {
                              const durationUnit = form.getValues('durationUnit') || 'days';
                              const availableUnits = getAvailableUnits(date, endDate);
                              
                              if (availableUnits.includes(durationUnit)) {
                                const durationValue = calculateDurationInUnit(date, endDate, durationUnit);
                                form.setValue('duration', durationValue);
                              } else {
                                const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                const durationValue = calculateDurationInUnit(date, endDate, newUnit);
                                form.setValue('durationUnit', newUnit);
                                form.setValue('duration', durationValue);
                              }
                            }
                          }
                        }}
                        disabled={(date) => date < new Date("1900-01-01")}
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={2100}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            field.onChange(today);
                            setStartDateOpen(false);
                            // In Duration mode: Recalculate end date
                            if (!isEndDateMode) {
                              const duration = form.getValues('duration');
                              const durationUnit = form.getValues('durationUnit') || 'days';
                              if (duration && duration > 0) {
                                const calculatedEndDate = addDurationToDate(today, duration, durationUnit);
                                form.setValue('endDate', calculatedEndDate, { shouldValidate: false });
                              }
                            }
                            // In End Date mode: Recalculate duration
                            else {
                              const endDate = form.getValues('endDate');
                              if (endDate) {
                                const durationUnit = form.getValues('durationUnit') || 'days';
                                const availableUnits = getAvailableUnits(today, endDate);
                                
                                if (availableUnits.includes(durationUnit)) {
                                  const durationValue = calculateDurationInUnit(today, endDate, durationUnit);
                                  form.setValue('duration', durationValue);
                                } else {
                                  const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                  const durationValue = calculateDurationInUnit(today, endDate, newUnit);
                                  form.setValue('durationUnit', newUnit);
                                  form.setValue('duration', durationValue);
                                }
                              }
                            }
                          }}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'endDate':
        const startDate = form.watch('startDate');
        const isEndDateDisabled = !startDate || !isEndDateMode;
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{label}</FormLabel>
                <Popover 
                  open={endDateOpen} 
                  onOpenChange={(open) => {
                    setEndDateOpen(open);
                    if (open) {
                      // When opening, set the calendar month to the selected date, start date, or today
                      setEndDateMonth(field.value || startDate || new Date());
                    }
                  }}
                >
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        disabled={isEndDateDisabled}
                        className={cn(
                          "w-full pl-3 text-left font-normal transition-all",
                          !field.value && "text-muted-foreground",
                          isEndDateDisabled && "opacity-50 cursor-not-allowed bg-muted"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>
                            {!startDate ? "Set start date first" : !isEndDateMode ? "Calculated from duration" : "Pick a date"}
                          </span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <div className="flex flex-col">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        month={endDateMonth}
                        onMonthChange={setEndDateMonth}
                        onSelect={(date) => {
                          field.onChange(date);
                          setEndDateOpen(false);
                          // In End Date mode: Update duration (read-only) when end date changes
                          if (date && startDate && isEndDateMode) {
                            const currentUnit = form.watch('durationUnit') || 'days';
                            const availableUnits = getAvailableUnits(startDate, date);
                            
                            // If current unit is available, use it
                            if (availableUnits.includes(currentUnit)) {
                              const durationValue = calculateDurationInUnit(startDate, date, currentUnit);
                              form.setValue('duration', durationValue);
                            } else {
                              // Auto-switch to the first available unit (or days as fallback)
                              const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                              const durationValue = calculateDurationInUnit(startDate, date, newUnit);
                              form.setValue('durationUnit', newUnit);
                              form.setValue('duration', durationValue);
                            }
                          }
                        }}
                        disabled={(date) => {
                          // Disable dates before 1900
                          if (date < new Date("1900-01-01")) return true;
                          // Disable dates on or before start date
                          if (startDate) {
                            const nextDay = new Date(startDate);
                            nextDay.setDate(nextDay.getDate() + 1);
                            nextDay.setHours(0, 0, 0, 0);
                            return date < nextDay;
                          }
                          return false;
                        }}
                        captionLayout="dropdown"
                        fromYear={startDate?.getFullYear() || 1900}
                        toYear={2100}
                        fromMonth={startDate || undefined}
                        initialFocus
                      />
                      <div className="border-t border-border p-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            const today = new Date();
                            // Only allow today if it's after start date
                            if (startDate) {
                              const nextDay = new Date(startDate);
                              nextDay.setDate(nextDay.getDate() + 1);
                              nextDay.setHours(0, 0, 0, 0);
                              if (today >= nextDay) {
                                field.onChange(today);
                                setEndDateOpen(false);
                                // In End Date mode: Update duration (read-only) when end date changes
                                if (isEndDateMode) {
                                  const currentUnit = form.watch('durationUnit') || 'days';
                                  const availableUnits = getAvailableUnits(startDate, today);
                                  
                                  // If current unit is available, use it
                                  if (availableUnits.includes(currentUnit)) {
                                    const durationValue = calculateDurationInUnit(startDate, today, currentUnit);
                                    form.setValue('duration', durationValue);
                                  } else {
                                    // Auto-switch to the first available unit (or days as fallback)
                                    const newUnit = availableUnits.length > 0 ? availableUnits[0] : 'days';
                                    const durationValue = calculateDurationInUnit(startDate, today, newUnit);
                                    form.setValue('durationUnit', newUnit);
                                    form.setValue('duration', durationValue);
                                  }
                                }
                              }
                            }
                          }}
                          disabled={!startDate || new Date() <= startDate}
                        >
                          Today
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'duration':
        const startDateForDuration = form.watch('startDate');
        const endDateForDuration = form.watch('endDate');
        const currentDuration = form.watch('duration');
        const currentDurationUnit = form.watch('durationUnit') || 'days';
        const isDurationDisabled = !startDateForDuration || isEndDateMode;
        
        // Get available units based on current end date (for End Date mode)
        const availableUnitsForSelect = isEndDateMode && startDateForDuration && endDateForDuration
          ? getAvailableUnits(startDateForDuration, endDateForDuration)
          : ['days', 'weeks', 'months', 'years'];
        
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="duration"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <div className="flex gap-2 items-end">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Duration
                    </label>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max={getMaxDuration(currentDurationUnit)}
                        disabled={isDurationDisabled}
                        placeholder={
                          !startDateForDuration 
                            ? "Set start date first" 
                            : isEndDateMode 
                              ? "Calculated from end date" 
                              : "Enter duration"
                        }
                        className={cn(
                          "transition-all",
                          isDurationDisabled && "opacity-50 cursor-not-allowed bg-muted"
                        )}
                        value={field.value || ''}
                        onChange={(e) => {
                          let value = e.target.value === '' ? undefined : parseInt(e.target.value);
                          const maxValue = getMaxDuration(currentDurationUnit);
                          
                          // Cap the value if it exceeds the max
                          if (value && value > maxValue) {
                            value = maxValue;
                            setShowDurationCapWarning(true);
                            setTimeout(() => setShowDurationCapWarning(false), 3000);
                          }
                          
                          field.onChange(value);
                          // In Duration mode: Update end date when duration changes
                          if (value && value > 0 && startDateForDuration && !isEndDateMode) {
                            const newEndDate = addDurationToDate(startDateForDuration, value, currentDurationUnit);
                            form.setValue('endDate', newEndDate);
                          }
                        }}
                      />
                    </FormControl>
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                      Unit
                    </label>
                    <Select
                    disabled={!startDateForDuration}
                    value={currentDurationUnit}
                    onValueChange={(value: DurationUnit) => {
                      form.setValue('durationUnit', value);
                      
                      if (isEndDateMode) {
                        // In End Date mode: Recalculate duration in new unit
                        if (startDateForDuration && endDateForDuration) {
                          const newDurationValue = calculateDurationInUnit(startDateForDuration, endDateForDuration, value);
                          form.setValue('duration', newDurationValue);
                        }
                      } else {
                        // In Duration mode: Cap duration if it exceeds max for new unit, then recalculate end date
                        if (currentDuration && currentDuration > 0 && startDateForDuration) {
                          const maxValue = getMaxDuration(value);
                          let cappedDuration = currentDuration;
                          
                          if (currentDuration > maxValue) {
                            cappedDuration = maxValue;
                            form.setValue('duration', cappedDuration);
                            setShowDurationCapWarning(true);
                            setTimeout(() => setShowDurationCapWarning(false), 3000);
                          }
                          
                          const newEndDate = addDurationToDate(startDateForDuration, cappedDuration, value);
                          form.setValue('endDate', newEndDate);
                        }
                      }
                    }}
                  >
                    <SelectTrigger className={cn(
                      "w-full transition-all",
                      !startDateForDuration && "opacity-50 cursor-not-allowed"
                    )}>
                      <SelectValue>
                        {currentDuration && currentDuration > 0 
                          ? formatDurationUnit(currentDuration, currentDurationUnit)
                          : currentDurationUnit
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableUnitsForSelect.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit.charAt(0).toUpperCase() + unit.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  </div>
                </div>
                {showDurationCapWarning && (
                  <p className="text-xs text-muted-foreground animate-in fade-in slide-in-from-top-1 duration-300">
                    Duration capped at {getMaxDuration(currentDurationUnit)} {formatDurationUnit(getMaxDuration(currentDurationUnit), currentDurationUnit)} (maximum allowed)
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectOwner':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectOwner"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={
                      fetchedEmployees.length > 0
                        ? fetchedEmployees.map(employee => ({ 
                            value: employee.id, 
                            label: employee.name 
                          }))
                        : projectManagerOptions.map(option => ({ 
                            value: option.toLowerCase().replace(' ', '-'), 
                            label: option 
                          }))
                    }
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={isLoadingEmployees ? "Loading employees..." : "Select project owner"}
                    searchPlaceholder="Search project owner..."
                    emptyMessage="No project owner found."
                    disabled={isLoadingEmployees}
                  />
                </FormControl>
                <FormDescription>
                  Business stakeholder with ultimate accountability
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectManager':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectManager"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Combobox
                    options={
                      fetchedEmployees.length > 0
                        ? fetchedEmployees.map(employee => ({ 
                            value: employee.id, 
                            label: employee.name 
                          }))
                        : projectManagerOptions.map(option => ({ 
                            value: option.toLowerCase().replace(' ', '-'), 
                            label: option 
                          }))
                    }
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={isLoadingEmployees ? "Loading employees..." : "Select project manager"}
                    searchPlaceholder="Search project manager..."
                    emptyMessage="No project manager found."
                    disabled={isLoadingEmployees}
                  />
                </FormControl>
                <FormDescription>
                  Primary person responsible for day-to-day execution
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'projectManagers':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="projectManagers"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <MultiSelect
                    options={
                      fetchedEmployees.length > 0
                        ? fetchedEmployees.map(employee => ({ 
                            value: employee.id, 
                            label: employee.name 
                          }))
                        : projectManagerOptions.map(option => ({ 
                            value: option.toLowerCase().replace(' ', '-'), 
                            label: option 
                          }))
                    }
                    placeholder={isLoadingEmployees ? "Loading employees..." : "Select additional managers..."}
                    emptyMessage="No project managers found."
                    value={field.value || []}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormDescription>
                  Additional managers for large projects or sub-teams
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'clients':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="clients"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <MultiSelectWithFavorites
                    options={
                      fetchedClients.length > 0
                        ? fetchedClients.map(client => ({ 
                            value: client.id, 
                            label: client.name 
                          }))
                        : clientOptions.map(client => ({ 
                            value: client.toLowerCase().replace(/\s+/g, '-'), 
                            label: client 
                          }))
                    }
                    placeholder={isLoadingClients ? "Loading clients..." : "Select clients..."}
                    emptyMessage="No clients found."
                    value={field.value || []}
                    onValueChange={field.onChange}
                    disabled={isLoadingClients}
                  />
                </FormControl>
                <FormDescription>
                  Client(s) for whom the project is being delivered
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'currencyCode':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <Combobox
                    options={[
                      { value: 'GBP', label: 'GBP - British Pound' },
                      { value: 'USD', label: 'USD - US Dollar' },
                      { value: 'EUR', label: 'EUR - Euro' },
                      { value: 'AUD', label: 'AUD - Australian Dollar' },
                      { value: 'CAD', label: 'CAD - Canadian Dollar' },
                      { value: 'CHF', label: 'CHF - Swiss Franc' },
                      { value: 'JPY', label: 'JPY - Japanese Yen' },
                      { value: 'CNY', label: 'CNY - Chinese Yuan' },
                    ]}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Select currency"
                    searchPlaceholder="Search currency..."
                    emptyMessage="No currency found."
                  />
                </FormControl>
                <FormDescription>
                  Currency for financial tracking and budget reporting
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'description':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <TextareaWithCount 
                    placeholder="Provide a detailed description of the project..."
                    className="min-h-24"
                    maxLength={integrationSettings.descriptionMaxLength}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'budget':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="budget"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{label}</FormLabel>
                <FormControl>
                  <Input 
                    type="text"
                    inputMode="decimal"
                    placeholder="Enter budget amount" 
                    value={formatBudgetDisplay(field.value || '')}
                    onFocus={(e) => {
                      // Show raw value on focus for easier editing
                      e.target.value = parseBudgetValue(e.target.value);
                    }}
                    onBlur={(e) => {
                      // Format with thousand separators on blur
                      const rawValue = parseBudgetValue(e.target.value);
                      if (rawValue) {
                        // Ensure 2 decimal places on blur if there's a decimal
                        const parts = rawValue.split('.');
                        let formattedValue = rawValue;
                        if (parts[1] !== undefined) {
                          formattedValue = parts[0] + '.' + parts[1].padEnd(2, '0').slice(0, 2);
                        }
                        field.onChange(formattedValue);
                      }
                      field.onBlur();
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Remove any commas that might have been pasted
                      value = value.replace(/,/g, '');
                      // Only allow digits and decimal point (no minus sign for budget)
                      value = value.replace(/[^0-9.]/g, '');
                      // Ensure only one decimal point
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }
                      // Limit to 16 digits before decimal for DECIMAL(18,2)
                      // Value is sent as string to backend to preserve full precision
                      if (parts[0] && parts[0].length > 16) {
                        value = parts[0].slice(0, 16) + (parts[1] !== undefined ? '.' + parts[1] : '');
                      }
                      // Limit to 2 decimal places
                      if (parts[1] && parts[1].length > 2) {
                        value = parts[0] + '.' + parts[1].slice(0, 2);
                      }
                      field.onChange(value);
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Optional: Enter the project budget in your local currency
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      case 'tags':
        return wrapFieldWithHighlight(fieldKey,
          <FormField
            control={form.control}
            name="tags"
            render={({ field }) => (
              <FormItem>
                <LabelWithTooltip label={label} isFromTemplate={isFromTemplate} />
                <FormControl>
                  <MultiSelect
                    options={tagOptions.map(tag => ({ value: tag.toLowerCase(), label: tag }))}
                    placeholder="Select tags..."
                    emptyMessage="No tags found."
                    value={field.value || []}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                <FormDescription>
                  Add tags to categorize and organize this project
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col h-full">
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
          {/* Template Selection - Always shown first in Quick Create mode */}
          {quickCreateMode && templates.length > 0 && onTemplateSelect && (
            <Collapsible open={sectionsOpen.templates} onOpenChange={() => toggleSection('templates')}>
              <div className="rounded-lg border bg-card">
                <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <h3 className="text-lg font-semibold">Project Templates</h3>
                    {/* Template Summary - Only visible when collapsed and template is selected */}
                    {!sectionsOpen.templates && templateSummary && (
                      <>
                        <span className="text-muted-foreground">|</span>
                        <span className="text-sm text-muted-foreground truncate">
                          Using: {templateSummary}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <ChevronDown className={cn(
                      "h-5 w-5 transition-transform",
                      sectionsOpen.templates && "rotate-180"
                    )} />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    {/* Template Selector with Manage Templates aligned right */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-2 flex-1 max-w-[50%]">
                        <LabelWithTooltip 
                          label="Select Template" 
                          tooltip="Choose a template to start with, or create your own"
                        />
                        <Combobox
                          options={[
                            { value: 'none', label: 'None (blank project)' },
                            ...templates.map(template => ({
                              value: template.id,
                              label: template.name,
                              description: template.description,
                              isFavorite: template.isFavorite
                            }))
                          ]}
                          value={selectedTemplate || 'none'}
                          onValueChange={(value) => {
                            onTemplateSelect(value);
                          }}
                          placeholder="Select template"
                          searchPlaceholder="Search templates..."
                          emptyMessage="No template found."
                          className={cn(
                            "w-full transition-all",
                            selectedTemplate && selectedTemplate !== 'none' && "border-primary/50 bg-primary/5"
                          )}
                          renderOption={(option) => (
                            <div className="flex items-start gap-2 w-full">
                              {option.isFavorite && (
                                <Star className="h-3 w-3 fill-warning-500 text-warning-500 shrink-0 mt-0.5" />
                              )}
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="truncate">{option.label}</span>
                                {option.description && (
                                  <span className="text-xs text-muted-foreground line-clamp-2">{option.description}</span>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      </div>

                      <div className="space-y-2 flex-shrink-0">
                        <LabelWithTooltip 
                          label="Template Management" 
                          tooltip="Edit and organize your project templates"
                          align="end"
                        />
                        <div className="flex items-center h-10">
                          {onManageTemplates && (
                            <span
                              onClick={onManageTemplates}
                              className="text-sm font-medium text-primary hover:underline cursor-pointer"
                            >
                              Manage Templates
                            </span>
                          )}
                        </div>
                      </div>
                    </div>


                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Quick Create Section - Only shown in Quick Create mode when NOT showing full details AND there are optional fields */}
          {quickCreateMode && !showFullDetails && hasOptionalFields && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-primary/5 border-primary/20 p-4 space-y-4 animate-in fade-in-0 duration-300">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      Quick Create
                      <Badge variant="secondary" className="text-xs font-normal">Fast Track</Badge>
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Complete the required fields below to create your project quickly.
                    </p>
                  </div>
                </div>

                {/* Dynamically render all required fields */}
                <div className="space-y-4">
                  {quickCreateFields
                    .filter(fieldKey => !['startDate', 'endDate', 'duration'].includes(fieldKey))
                    .map((fieldKey) => (
                      <div key={fieldKey}>
                        {renderField(fieldKey)}
                      </div>
                    ))}
                  
                  {/* Timeline section - show as grouped section when timeline completion is required */}
                  {timelineSettings.timelineCompletionRequired && (
                    <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold">Timeline & Scheduling</h4>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-muted-foreground">
                                  {isEndDateMode ? 'End Date' : 'Duration'}
                                </span>
                                <Switch
                                  checked={!isEndDateMode}
                                  onCheckedChange={(checked) => setIsEndDateMode(!checked)}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="left" align="center" className="max-w-[200px]">
                              <p>Switch between entering the End Date or a Duration</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {renderField('startDate')}
                        {renderField('endDate')}
                        {renderField('duration')}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Progress indicator for Quick Create */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={cn(
                      "font-medium transition-colors",
                      progress === 100 ? "text-success" : canSubmit ? "text-primary" : "text-muted-foreground"
                    )}>
                      {progress === 100 
                        ? "✓ All fields complete!" 
                        : canSubmit 
                          ? "✓ All required fields complete! Ready to create."
                          : `Complete ${localIncompleteFields.length} required field${localIncompleteFields.length !== 1 ? 's' : ''} to continue`}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{progress}%</span>
                      <Progress value={progress} className="w-24 h-2" />
                    </div>
                  </div>
                  {localIncompleteFields.length > 0 && !canSubmit && (
                    <p className="text-xs text-muted-foreground">
                      Missing: {localIncompleteFields.slice(0, 3).join(', ')}
                      {localIncompleteFields.length > 3 && ` +${localIncompleteFields.length - 3} more`}
                    </p>
                  )}
                </div>
              </div>

              {/* Progressive Disclosure Card - Only shown when required fields are complete */}
              {canSubmit && (
                <div className="rounded-lg border bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 p-6 space-y-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <h4 className="text-base font-semibold text-foreground mb-1">
                          Need More Control?
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Add optional details to unlock:
                        </p>
                      </div>
                      
                      <ul className="space-y-2 text-sm text-foreground">
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Budget tracking & cost management</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Resource allocation & team planning</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Advanced scheduling & milestones</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-primary mt-0.5">•</span>
                          <span>Custom fields & metadata</span>
                        </li>
                      </ul>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                        <Button
                          type="button"
                          onClick={onShowFullDetails}
                          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                        >
                          Add Optional Details
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          or continue with Quick Create
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* General Info Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.general} onOpenChange={() => toggleSection('general')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <h3 className="text-lg font-semibold">General Information</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.general && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="projectName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('projectName')}</FormLabel>
                          <FormControl>
                            <Input placeholder="Enter project name" maxLength={integrationSettings.projectNameMaxLength} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectCode"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center gap-1.5">
                            <FormLabel>{getFieldLabel('projectCode')}</FormLabel>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 rounded-sm text-muted-foreground hover:text-foreground"
                                  onClick={() => {
                                    const projectName = form.getValues('projectName');
                                    if (projectName) {
                                      const generatedCode = generateProjectCode(projectName, integrationSettings.projectCodeMaxLength);
                                      form.setValue('projectCode', generatedCode, { shouldValidate: true });
                                    }
                                  }}
                                >
                                  <WandSparkles className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="text-sm">Auto-generate code from project name</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                          <FormControl>
                            <Input
                              placeholder="e.g., PRJ-2024-001"
                              maxLength={integrationSettings.projectCodeMaxLength}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel>{getFieldLabel('description')}</FormLabel>
                            {quickCreateMode && !showAiPanel && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowAiPanel(true)}
                                className="h-7 text-xs text-primary hover:text-primary/80"
                              >
                                <Sparkles className="h-3 w-3 mr-1" />
                                Generate with AI
                              </Button>
                            )}
                          </div>
                          <FormControl>
                            <TextareaWithCount 
                              placeholder="Provide a detailed description of the project..."
                              className="min-h-24"
                              maxLength={integrationSettings.descriptionMaxLength}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Inline AI Panel */}
                    {showAiPanel && (
                      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-5 w-5 text-primary" />
                            <div>
                              <h4 className="font-semibold text-sm">AI Project Assistant</h4>
                              <p className="text-xs text-muted-foreground">
                                Describe your project and I'll help fill in the details
                              </p>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowAiPanel(false)}
                            className="h-7 w-7 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="ai-description" className="text-sm font-medium">
                              What are you building? *
                            </Label>
                            <Textarea
                              id="ai-description"
                              placeholder="e.g., A customer portal with authentication, dashboard, and reporting features..."
                              value={aiProjectDescription}
                              onChange={(e) => setAiProjectDescription(e.target.value)}
                              className="min-h-20 resize-none bg-background"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label htmlFor="ai-objectives" className="text-sm font-medium">
                                Key Objectives
                              </Label>
                              <Input
                                id="ai-objectives"
                                placeholder="e.g., Improve customer satisfaction..."
                                value={aiObjectives}
                                onChange={(e) => setAiObjectives(e.target.value)}
                                className="bg-background"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="ai-timeline" className="text-sm font-medium">
                                Timeline
                              </Label>
                              <Input
                                id="ai-timeline"
                                placeholder="e.g., 6 months, Q2 2024..."
                                value={aiTimeline}
                                onChange={(e) => setAiTimeline(e.target.value)}
                                className="bg-background"
                              />
                            </div>
                          </div>

                          <Button
                            type="button"
                            onClick={handleAiGenerate}
                            disabled={!aiProjectDescription.trim() || aiGenerating}
                            className="w-full"
                          >
                            {aiGenerating ? (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Generate Project Details
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="rounded-md bg-background/50 p-3 text-xs text-muted-foreground">
                          <p className="font-medium mb-1">AI will help you with:</p>
                          <ul className="space-y-0.5 list-disc list-inside">
                            <li>Project name and code generation</li>
                            <li>Detailed description and objectives</li>
                            <li>Timeline and milestone suggestions</li>
                            <li>Resource allocation recommendations</li>
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('status')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={statusOptions}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select status"
                              searchPlaceholder="Search status..."
                              emptyMessage="No status found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('priority')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={priorityOptions.map(option => ({ 
                                value: option.toLowerCase(), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select priority"
                              searchPlaceholder="Search priority..."
                              emptyMessage="No priority found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{getFieldLabel('type')}</FormLabel>
                          <FormControl>
                            <Combobox
                              options={typeOptions.map(option => ({ 
                                value: option.toLowerCase().replace(' ', '-'), 
                                label: option 
                              }))}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select type"
                              searchPlaceholder="Search type..."
                              emptyMessage="No type found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{getFieldLabel('location')}</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter project location" {...field} />
                        </FormControl>
                        <FormDescription>
                          Physical or organizational location of the project
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Timeline Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.timeline} onOpenChange={() => toggleSection('timeline')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <h3 className="text-lg font-semibold">Timeline</h3>
                  {/* Timeline Summary - Only visible when collapsed and dates are filled */}
                  {!sectionsOpen.timeline && timelineSummary && (
                    <>
                      <span className="text-muted-foreground">|</span>
                      {/* Full format on larger screens */}
                      <span className="hidden lg:block text-sm text-muted-foreground truncate">
                        {timelineSummary.full}
                      </span>
                      {/* Compact format on smaller screens */}
                      <span className="lg:hidden text-sm text-muted-foreground truncate">
                        {timelineSummary.compact}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  {/* Mode Switch - Always visible when expanded */}
                  {sectionsOpen.timeline && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <span className="text-sm text-muted-foreground">
                              {isEndDateMode ? 'End Date' : 'Duration'}
                            </span>
                            <Switch
                              checked={!isEndDateMode}
                              onCheckedChange={(checked) => setIsEndDateMode(!checked)}
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="left" align="center" className="max-w-[200px]">
                          <p>Switch between entering the End Date or a Duration</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <ChevronDown className={cn(
                    "h-5 w-5 transition-transform",
                    sectionsOpen.timeline && "rotate-180"
                  )} />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {renderField('startDate')}
                    {renderField('endDate')}
                    {renderField('duration')}
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Management Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.management} onOpenChange={() => toggleSection('management')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <h3 className="text-lg font-semibold">Management</h3>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.management && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  <FormField
                    control={form.control}
                    name="projectOwner"
                    render={({ field }) => (
                      <FormItem>
                        <LabelWithTooltip 
                          label={getFieldLabel('projectOwner')} 
                          tooltip="Business stakeholder with ultimate accountability and budget approval authority"
                        />
                        <FormControl>
                          <Combobox
                            options={
                              fetchedEmployees.length > 0
                                ? fetchedEmployees.map(employee => ({ 
                                    value: employee.id, 
                                    label: employee.name 
                                  }))
                                : projectManagerOptions.map(option => ({ 
                                    value: option.toLowerCase().replace(' ', '-'), 
                                    label: option 
                                  }))
                            }
                            value={field.value}
                            onValueChange={field.onChange}
                            placeholder={isLoadingEmployees ? "Loading employees..." : "Select project owner"}
                            searchPlaceholder="Search project owner..."
                            emptyMessage="No project owner found."
                            disabled={isLoadingEmployees}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Project Managers - Side by Side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="projectManager"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip 
                            label={getFieldLabel('projectManager')} 
                            tooltip="Primary person responsible for day-to-day project execution"
                          />
                          <FormControl>
                            <Combobox
                              options={
                                fetchedEmployees.length > 0
                                  ? fetchedEmployees.map(employee => ({ 
                                      value: employee.id, 
                                      label: employee.name 
                                    }))
                                  : projectManagerOptions.map(option => ({ 
                                      value: option.toLowerCase().replace(' ', '-'), 
                                      label: option 
                                    }))
                              }
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder={isLoadingEmployees ? "Loading employees..." : "Select project manager"}
                              searchPlaceholder="Search project manager..."
                              emptyMessage="No project manager found."
                              disabled={isLoadingEmployees}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectManagers"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip 
                            label={getFieldLabel('projectManagers')} 
                            tooltip="Additional project managers for large projects or sub-teams"
                            align="end"
                          />
                          <FormControl>
                            <MultiSelect
                              options={
                                fetchedEmployees.length > 0
                                  ? fetchedEmployees.map(employee => ({ 
                                      value: employee.id, 
                                      label: employee.name 
                                    }))
                                  : projectManagerOptions.map(option => ({ 
                                      value: option.toLowerCase().replace(' ', '-'), 
                                      label: option 
                                    }))
                              }
                              placeholder={isLoadingEmployees ? "Loading employees..." : "Select additional managers..."}
                              emptyMessage="No project managers found."
                              value={field.value || []}
                              onValueChange={field.onChange}
                              maxDisplay={1}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Currency and Budget - Side by Side */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="currencyCode"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip 
                            label={getFieldLabel('currencyCode')} 
                            tooltip="Currency for financial tracking and budget reporting"
                          />
                          <FormControl>
                            <Combobox
                              options={[
                                { value: 'GBP', label: 'GBP - British Pound' },
                                { value: 'USD', label: 'USD - US Dollar' },
                                { value: 'EUR', label: 'EUR - Euro' },
                                { value: 'AUD', label: 'AUD - Australian Dollar' },
                                { value: 'CAD', label: 'CAD - Canadian Dollar' },
                                { value: 'CHF', label: 'CHF - Swiss Franc' },
                                { value: 'JPY', label: 'JPY - Japanese Yen' },
                                { value: 'CNY', label: 'CNY - Chinese Yuan' },
                              ]}
                              value={field.value}
                              onValueChange={field.onChange}
                              placeholder="Select currency"
                              searchPlaceholder="Search currency..."
                              emptyMessage="No currency found."
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="budget"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip 
                            label={getFieldLabel('budget')} 
                            tooltip="Optional: Enter the project budget in your local currency"
                            align="end"
                          />
                          <FormControl>
                            <Input 
                              type="text"
                              inputMode="decimal"
                              placeholder="Enter budget amount" 
                              value={formatBudgetDisplay(field.value || '')}
                              onFocus={(e) => {
                                // Show raw value on focus for easier editing
                                e.target.value = parseBudgetValue(e.target.value);
                              }}
                              onBlur={(e) => {
                                // Format with thousand separators on blur
                                const rawValue = parseBudgetValue(e.target.value);
                                if (rawValue) {
                                  // Ensure 2 decimal places on blur if there's a decimal
                                  const parts = rawValue.split('.');
                                  let formattedValue = rawValue;
                                  if (parts[1] !== undefined) {
                                    formattedValue = parts[0] + '.' + parts[1].padEnd(2, '0').slice(0, 2);
                                  }
                                  field.onChange(formattedValue);
                                }
                                field.onBlur();
                              }}
                              onChange={(e) => {
                                let value = e.target.value;
                                // Remove any commas that might have been pasted
                                value = value.replace(/,/g, '');
                                // Only allow digits and decimal point (no minus sign for budget)
                                value = value.replace(/[^0-9.]/g, '');
                                // Ensure only one decimal point
                                const parts = value.split('.');
                                if (parts.length > 2) {
                                  value = parts[0] + '.' + parts.slice(1).join('');
                                }
                                // Limit to 16 digits before decimal for DECIMAL(18,2)
                                // Value is sent as string to backend to preserve full precision
                                if (parts[0] && parts[0].length > 16) {
                                  value = parts[0].slice(0, 16) + (parts[1] !== undefined ? '.' + parts[1] : '');
                                }
                                // Limit to 2 decimal places
                                if (parts[1] && parts[1].length > 2) {
                                  value = parts[0] + '.' + parts[1].slice(0, 2);
                                }
                                field.onChange(value);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Clients */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="clients"
                      render={({ field }) => (
                        <FormItem>
                          <LabelWithTooltip 
                            label={getFieldLabel('clients')} 
                            tooltip="Select one or more clients associated with this project"
                          />
                          <FormControl>
                            <MultiSelect
                              options={
                                fetchedClients.length > 0
                                  ? fetchedClients.map(client => ({ 
                                      value: client.id, 
                                      label: client.name 
                                    }))
                                  : clientOptions.map(client => ({ 
                                      value: client.toLowerCase().replace(/\s+/g, '-'), 
                                      label: client 
                                    }))
                              }
                              placeholder={isLoadingClients ? "Loading clients..." : "Select clients..."}
                              emptyMessage="No clients found."
                              value={field.value || []}
                              onValueChange={field.onChange}
                              maxDisplay={1}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  </div>

                  <FormField
                    control={form.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <LabelWithTooltip 
                          label={getFieldLabel('tags')} 
                          tooltip="Add tags to categorize and organize this project"
                        />
                        <FormControl>
                          <MultiSelect
                            options={tagOptions.map(tag => ({ value: tag.toLowerCase(), label: tag }))}
                            placeholder="Select tags..."
                            emptyMessage="No tags found."
                            value={field.value || []}
                            onValueChange={field.onChange}
                            maxDisplay={1}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}

          {/* Custom Fields Section - Hidden in Quick Create mode unless showFullDetails is true OR there are no optional fields */}
          {(!quickCreateMode || showFullDetails || !hasOptionalFields) && (
          <Collapsible open={sectionsOpen.custom} onOpenChange={() => toggleSection('custom')}>
            <div className="rounded-lg border bg-card">
              <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Custom Fields</h3>
                  <Badge variant="secondary">{customFields.length}/20</Badge>
                </div>
                <ChevronDown className={cn(
                  "h-5 w-5 transition-transform",
                  sectionsOpen.custom && "rotate-180"
                )} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="p-4 pt-0 space-y-4">
                  {customFields.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <p className="mb-4">No custom fields added yet</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addCustomField}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Custom Field
                      </Button>
                    </div>
                  ) : (
                    <>
                      {customFields.map((field, index) => (
                        <div key={field.id} className="rounded-lg border p-4 space-y-3 bg-muted/30">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">Custom Field {index + 1}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeCustomField(field.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Field Key</label>
                              <Input
                                placeholder="field_name"
                                value={field.fieldKey}
                                onChange={(e) => updateCustomField(field.id, { fieldKey: e.target.value })}
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">Field Label</label>
                              <Input
                                placeholder="Display Name"
                                value={field.fieldLabel}
                                onChange={(e) => updateCustomField(field.id, { fieldLabel: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Field Type</label>
                            <Select
                              value={field.fieldType}
                              onValueChange={(value) => updateCustomField(field.id, { fieldType: value as any })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="text">Text</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="boolean">Boolean</SelectItem>
                                <SelectItem value="dropdown">Dropdown</SelectItem>
                                <SelectItem value="multiselect">Multi-select</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                      {customFields.length < 20 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addCustomField}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Another Custom Field
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
          )}
          </div>

          {/* Form Actions - Fixed at bottom of modal */}
          <div className="flex gap-3 pt-4 border-t bg-background shrink-0">
            {/* Left side - Mode toggle (only show if Quick Create is available) */}
            {quickCreateMode && hasOptionalFields && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (showFullDetails) {
                    onHideFullDetails?.();
                  } else {
                    onShowFullDetails?.();
                  }
                }}
                className="gap-2"
              >
                {showFullDetails ? (
                  <>
                    <ChevronLeft className="h-4 w-4" />
                    Back to Quick Create
                  </>
                ) : (
                  <>
                    Show All Fields
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            )}
            
            {/* Right side - Action buttons */}
            <div className="flex items-center gap-3 ml-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                aria-label="Cancel project creation (Esc)"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!isFormComplete || projectCodeExists || isCheckingProjectCode}
                className={cn(
                  "relative transition-all",
                  isFormComplete && !projectCodeExists && !isCheckingProjectCode && "shadow-md hover:shadow-lg"
                )}
                aria-label={
                  projectCodeExists 
                    ? "Cannot create project - code already exists" 
                    : isCheckingProjectCode 
                      ? "Checking project code..." 
                      : isFormComplete 
                        ? "Create project (Ctrl+Enter)" 
                        : "Complete required fields to create project"
                }
              >
                {quickCreateMode && canSubmit ? 'Create Project' : 'Save Project'}
                {isFormComplete && !projectCodeExists && !isCheckingProjectCode && (
                  <span className="ml-2 text-xs opacity-60">⌘↵</span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {/* Unsaved Changes Warning Dialog */}
      <AlertDialog open={showCancelWarning} onOpenChange={setShowCancelWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-900/20">
                <AlertTriangle className="h-5 w-5 text-warning-600 dark:text-warning-500" />
              </div>
              <div className="flex-1">
                <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
                <AlertDialogDescription>
                  You have unsaved changes in this form. What would you like to do?
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2 sm:gap-2">
            <AlertDialogCancel 
              onClick={() => setShowCancelWarning(false)}
              className="w-full m-0"
            >
              Go Back to Form
            </AlertDialogCancel>
            <div className="flex gap-2 w-full">
              <AlertDialogAction
                onClick={handleSaveAsDraft}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                Save as Draft
              </AlertDialogAction>
              <AlertDialogAction
                onClick={handleDiscardChanges}
                className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              >
                Discard Changes
              </AlertDialogAction>
            </div>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
